import type { FC, ReactElement } from 'react';
import { createPortal } from 'react-dom';

import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { IconName } from '@iconik-io/uad';

import { getRouteCollectionId } from '../../../ducks/pages/search/utils';
import { selectCollectionWithFallback } from '../../../ducks/route_params/selectors';
import { useAppSelector } from '../../../store/react_redux_typed';
import { isCollectionMappedToStorage } from '../../../utils/collections';
import type { DragImageProps } from '../../draggable_items/draggable_items';
import SearchItemsDragLabel from '../search_items_drag_label/search_items_drag_label';

import styles from './search_items_drag_image.module.css';

const SearchDragImage: FC<DragImageProps> = (props) => {
  const { items, show, altKey, copyOnly, dropTargetElement } = props;
  const lockMappedCollections = useAppSelector(
    (state) => state.settings.merged.lock_mapped_collections,
  );
  const sourceCollection = useAppSelector((state) =>
    selectCollectionWithFallback(state, getRouteCollectionId(state)),
  );

  let content = null;
  let assetsOnly;
  const hasSubclipAssetType = items?.some(({ type }) => type === 'SUBCLIP');
  const isCollectionsDropTargetObjectType =
    dropTargetElement?.object_type === OBJECT_TYPE_COLLECTIONS;
  const isSubclipDropTargetObjectType = dropTargetElement?.type === 'SUBCLIP';
  const isDisabledDropType =
    dropTargetElement?.type === 'LINK' ||
    isSubclipDropTargetObjectType ||
    (hasSubclipAssetType && !isCollectionsDropTargetObjectType); // Ensures a subclip type can be dropped only into collections.
  const hasLinkAssetType = items?.some(({ type }) => type === 'LINK');

  if (
    show &&
    items &&
    items.length &&
    !isDisabledDropType &&
    !hasLinkAssetType
  ) {
    const types = new Set(items.map((item) => item.object_type));
    if (types.size === 1 && types.has(OBJECT_TYPE_ASSETS)) {
      assetsOnly = true;
    }

    const isMappedCollection =
      isCollectionMappedToStorage(sourceCollection) && lockMappedCollections;

    const isCopyOnlyOrMappedCollection = copyOnly || isMappedCollection;
    const copyMode =
      (assetsOnly && altKey) || (assetsOnly && isCopyOnlyOrMappedCollection);

    let title = '';
    let description: string | ReactElement = '';
    let isActive = true;
    let iconName: IconName = 'versions';
    if (dropTargetElement) {
      if (dropTargetElement.object_type === OBJECT_TYPE_ASSETS) {
        title = `Add as a Version v.${
          dropTargetElement.versions
            ? dropTargetElement.versions.length + 1
            : '?'
        }`;
        description = 'Drop to merge';
        if (!altKey) {
          isActive = false;
          description = (
            <div>
              {'Press '}
              <span className={styles.drag_image_alt_icon} />
              {' to enable'}
            </div>
          );
        }
      }

      if (dropTargetElement.object_type === OBJECT_TYPE_COLLECTIONS) {
        const hasCollection = items.some(
          ({ object_type }) => object_type === OBJECT_TYPE_COLLECTIONS,
        );
        const dragCaption = hasCollection
          ? 'Move or copy to Collection'
          : 'Move or add to Collection';
        title = copyMode ? 'Add to Collection' : dragCaption;
        iconName = copyMode ? 'plus' : 'move';
        if (assetsOnly && !isCopyOnlyOrMappedCollection) {
          description = (
            <div>
              {altKey ? 'Release ' : 'Hold '}
              <span className={styles.drag_image_alt_icon} />
              {altKey ? ' to move' : ' to add'}
            </div>
          );
        }
      }
    }

    content = (
      <div className={styles.drag_image}>
        {dropTargetElement && props.withHint ? (
          <div className={styles.label}>
            <SearchItemsDragLabel
              title={title}
              description={description}
              iconName={iconName}
              isActive={isActive}
            />
          </div>
        ) : null}
        <div className={styles.drag_content}>
          <div>{props.children}</div>
          {items.length > 1 && (
            <>
              {items.length > 2 && <div className={styles.drag_bottom} />}
              <div className={styles.drag_bottom2} />
            </>
          )}
        </div>
      </div>
    );
  }

  return createPortal(content, props.element);
};

export default SearchDragImage;
